/*global Flybits, Flightkit, document*/
import { html, css, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import cfg from '../config/cfg';
import pkg from '../../package.json';
import Deferred from '../models/deferred';
import ActionLink from '../models/actionLink';
import ZoneAPI from '../apis/zone';
import Zone from '../models/zone';
import CtxAPI from '../apis/ctx';
import Logger from '../utils/logger';
import ZoneUtil from '../utils/zone';
import ObjUtil from '../utils/obj';
import { commaSeparatedArray, isStructuredCloneable, toPlainArray } from '../utils/attributes';

/**
 * Attribute value object.
 * @typedef AttributeValue
 * @memberof Flightkit.FlybitsSlot
 * @type Object
 * @property {string} attrName The name of the attribute value eg. `componentEngaged`.
 * @property {string} value The value of the attribute value eg. `true`.
 * @property {string[]} parameters The parameters of the attribute value eg. `['contentID1', 'btnID1', 'Accept Offer', 'primary-button']`.
 */
/**
 * Context data object, parsed for easier digestion.
 * @typedef CtxDataObject
 * @memberof Flightkit.FlybitsSlot
 * @type Object
 * @property {string} dataTypeID The data type ID of the context data object. eg. `ctx.flybits.battery`
 * @property {string} providerName The name of the provider that the context data object is associated with. If the dataTypeID is `ctx.flybits.battery` the provider name is `flybits`.
 * @property {string} pluginName The name of the plugin that the context data object is associated with. If the dataTypeID is `ctx.flybits.battery` the plugin name is `battery`.
 * @property {string} timestamp The timestamp of data report in epoch seconds.
 * @property {Flightkit.FlybitsSlot.AttributeValue[]} values The attribute values of the context data object.
 */
/**
 * Event payload for a {@link Flightkit.FlybitsSlot#flb-ctxreported | flb-ctxreported} event. This payload is simply the transformed version of the same {@link Flybits.evt.CtxReportEvent | CtxReportEvent} payload.
 * @typedef CtxReportedEvent
 * @memberof Flightkit.FlybitsSlot
 * @type {Object}
 * @property {string} status "success" or "failed" status of Context Data report attempt.
 * @property {Flightkit.FlybitsSlot.CtxDataObject[]} data Context Data objects that the attempt report was made for.
 * @property {Flybits.Validation} [validation] Error objects should the report have failed.
 */


/**
 * @class FlybitsSlot
 * @classdesc A FlybitsSlot is an HTML element used to embed full featured Flybits curated Content and UI within your application.  It can take the form of a widget within an existing page of your application or it can take up a page in its entirety.  It is used to render a Flybits Zone which is a composition of Modules (layouts) that can contain relevant personalized Flybits Content.
 * @memberof Flightkit
 * @extends external:LitElement
 * @example 
 * <!-- declarative approach -->
 * <flybits-slot ref-id="homepage"></flybits-slot>
 * @example
 * // imperative approach
 * const slot = new FlybitsSlot();
 * slot.init({
 *   refID: 'homepage'
 * })
 * @fires Flightkit.FlybitsSlot#action
 * @fires Flightkit.FlybitsSlot#zoneLoad
 * @fires Flightkit.FlybitsSlot#error
 */
export default  class FlybitsSlot extends LitElement {
  static get VERSION(){
    return pkg.version;
  }

  /**
   * @member {Object} modes Mode type constants.
   * @memberof Flightkit.FlybitsSlot
   * @static
   * @constant
   * @property {string} FIXEDHEIGHT Mode type string for fixed height FlybitsSlot instances where it is expected the implementing parent application will define a fixed height for the element within its own UI.
   * @property {string} DYNAMICHEIGHT Mode type string for dynamic height FlybitsSlot instances where height will automatically adjust based on Zone configuration and inner content height. 
   */
  static get modes(){
    return {
      FIXEDHEIGHT: 'fixedheight',
      DYNAMICHEIGHT: 'dynamicheight'
    }
  }

  static properties = {
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} host (HTML attribute: `host`) Flybits environment specific API host URL from which all API calls will be made against. 
     */
    host: {
      type: String
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} projectID (HTML attribute: `project-id`) Flybits project ID used to load tenant specific data. 
     */
    projectID: {
      type: String,
      attribute: 'project-id'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} renderURL (HTML attribute: `render-url`) Render layer application URL. 
     */
    renderURL: {
      type: String,
      attribute: 'render-url'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} refID (HTML attribute: `ref-id`) Flybits Zone Reference ID which can be found in the Experience Studio on the Concierge Management page.  Every FlybitsSlot can render up to one Zone which may contain one or more Modules.
     */
    refID: {
      type: String,
      attribute: 'ref-id'
    },
    pollingEnabled: {
      type: Boolean,
      attribute: 'polling-enabled'
    },
    pollingInterval: {
      type: Number,
      attribute: 'polling-interval'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} renderMode (HTML attribute: `render-mode`) Mode setting to control automated sizing functionality.  Refer to available render modes {@link Flightkit.FlybitsSlot.modes}.
     */
    renderMode: {
      type: String,
      attribute: 'render-mode'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string | HTMLElement} overlayOutlet (HTML attribute: `overlay-outlet`) FlybitsSlot elements can open {@link Flightkit.FlybitsOverlay|FlybitsOverlay} instances depending on ActionLinks that are intercepted from its internal children or externally from application logic.  By default these overlays are appended to the `document.body`.  This attribute allows for overriding and specifying a parent element that {@link Flightkit.FlybitsOverlay|FlybitsOverlays} are appended to if `document.body` is not preferred.
     * 
     * Parent elements can be defined by either a CSS [selector string](https://developer.mozilla.org/en-US/docs/Web/API/Document_object_model/Locating_DOM_elements_using_selectors) or an [HTMLElement instance](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement).
     * @example
     * <!-- declarative approach -->
     * <div class="home">
     *   <div class="container"></div>
     * </div>
     * <flybits-slot ref-id="homepage" overlay-outlet=".home .container"></flybits-slot>
     * @example 
     * // imperative approach
     * const parentElem = document.createElement('div')
     * parentElem.classList.add('container');
     *
     * const slot = new FlybitsSlot();
     * slot.init({
     *   refID: 'homepage',
     *   overlayOutlet: parentElem
     * })
     */
    overlayOutlet: {
      type: String | HTMLElement,
      attribute: 'overlay-outlet'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {boolean} overrideOverlay (HTML attribute: `override-overlay`) By default FlybitsSlot elements will handle {@link Flightkit.ActionLink.protocol|DETAILS} and {@link Flightkit.ActionLink.protocol|CONCIERGE} Action Event types out of the box by opening them in {@link Flightkit.FlybitsOverlay|FlybitsOverlays}.  However if you would like to handle these manually in your application logic, set this flag to `true`.
     */
    overrideOverlay: {
      type: Boolean,
      attribute: 'override-overlay'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {boolean} overrideURLs (HTML Attribute: `override-urls`) By default FlybitsSlot elements will handle {@link Flightkit.ActionLink.protocol|HTTPS}, {@link Flightkit.ActionLink.protocol|BROWSER}, and {@link Flightkit.ActionLink.protocol|INAPPBROWSER} Action Event types out of the box by either open a new tab/window to load the URL, or redirect the current tab/window in the case of {@link Flightkit.ActionLink.protocol|INAPPBROWSER}. However if you would like to handle these manually in your application logic, set this flag to `true`.
     */
    overrideURLs: {
      type: Boolean,
      attribute: 'override-urls'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {boolean} overrideActions (HTML Attribute: `override-actions`) By default FlybitsSlot elements will handle certain Action Event types automatically:
     * - {@link Flightkit.ActionLink.protocol|DETAILS}
     * - {@link Flightkit.ActionLink.protocol|CONCIERGE}
     * - {@link Flightkit.ActionLink.protocol|HTTPS}
     * - {@link Flightkit.ActionLink.protocol|BROWSER}
     * - {@link Flightkit.ActionLink.protocol|INAPPBROWSER}
     * 
     * If you would like to handle all these manually in your application logic, set this flag to `true`.
     * > **note:** {@link Flightkit.FlybitsSlot#overrideActions|overrideActions}, {@link Flightkit.FlybitsSlot#overrideURLs|overrideURLs}, and {@link Flightkit.FlybitsSlot#overrideOverlay|overrideOverlay} are considered in a **logical OR'd** expression.  Thus if {@link Flightkit.FlybitsSlot#overrideURLs|overrideURLs} was `false` but {@link Flightkit.FlybitsSlot#overrideActions|overrideActions} is `true`, FlybitsSlot will **not** automatically handle URL based Action Event links.  
     * 
     * > The inverse also applies, where if {@link Flightkit.FlybitsSlot#overrideURLs|overrideURLs} is `true` but {@link Flightkit.FlybitsSlot#overrideActions|overrideActions} is `false`, FlybitsSlot will again **not** automatically handle URL based Action Event links.
     */
    overrideActions: {
      type: Boolean,
      attribute: 'override-actions'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {boolean} [overlayDismissUI=false] (HTML attribute: `overlay-dismiss-ui`) By default FlybitsSlot elements will handle {@link Flightkit.ActionLink.protocol|DETAILS} and {@link Flightkit.ActionLink.protocol|CONCIERGE} Action Event types out of the box by opening them in {@link Flightkit.FlybitsOverlay|FlybitsOverlays}.  The expectation is that each overlay Content will have its own overlay dismissal UI element.  However if you would like a default overlay close button, set this flag to `true`.
     */
    overlayDismissUI: {
      type: Boolean,
      attribute: 'overlay-dismiss-ui'
    },
    isLoadingOpen: {
      type: Boolean,
      attribute: 'show-loading'
    },
    isErrorOpen: {
      type: Boolean,
      attribute: 'show-error'
    },
    hasLoadingSlot: {
      type: Boolean
    },
    hasErrorSlot: {
      type: Boolean
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} [logLevel=errors] Logging mode setting.  By default FlybitsSlot will only output errors to the console. Changing this attribute to `verbose` will increase verbosity and output logs with much more detail for debugging purposes.  All available values can be found {@link Flightkit.Logger.modes|here}.
     */
    logLevel: {
      type: String,
      attribute: 'log-level'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} [localization=disabled] Localization control setting.  By default FlybitsSlot will render english content from Flybits Experience Studio. If this attribute is set to `auto` it will try to detect the browser langauge and render corresponding localization of Flybits content if available. Alternatively if you explicitly pass the [two character locale key](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) into this attribute (eg. en, fr, es, etc) the content will render accordingly.
     */
    localization: {
      type: String,
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string} [textDirection=unset] Manual override for global text direction.  When unset, the text direction will be automatically set based on the {@link Flightkit.FlybitsSlot#localization|localization} language set.  Accepted values include: `ltr`, `rtl`, and `auto`.
     */
    textDirection: {
      type: String,
      attribute: 'text-direction'
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string[]} [exclude=[]] (HTML attribute: `exclude`) Array of content labels to exclude from rendered Zone content. Values are forwarded to the render iframe via {@link Flightkit.FlybitsSlot#injectData|injectData} as `excludeContentLabels`.
     *
     * When set as an HTML attribute, provide a comma-separated list (whitespace around labels is ignored). Framework property bindings may pass a `string[]` directly.
     *
     * Reactive framework proxies (e.g. Vue `data()` arrays) are normalized to plain arrays before iframe injection so `postMessage` does not throw `DataCloneError`.
     * @example
     * <!-- declarative (vanilla HTML) -->
     * <flybits-slot ref-id="homepage" exclude="special,offer"></flybits-slot>
     * @example
     * <!-- Vue -->
     * <flybits-slot ref-id="homepage" :exclude="['special','offer']"></flybits-slot>
     * @example
     * // React
     * <flybits-slot ref-id="homepage" exclude={['special', 'offer']}></flybits-slot>
     * @example
     * // imperative
     * const slot = new FlybitsSlot();
     * slot.init({
     *   refID: 'homepage',
     *   exclude: ['special', 'offer']
     * });
     */
    exclude: {
      type: Array,
      attribute: 'exclude',
      converter: commaSeparatedArray
    },
    /**
     * @instance 
     * @memberof Flightkit.FlybitsSlot
     * @member {function} [flbCtxReported] Callback function to be invoked when a {@link Flightkit.FlybitsSlot#event:flb-ctxreported | flb-ctxreported} event occurs.  Useful for the React convention of event handling.
     * @example
     * <!-- React -->
     * <flybits-slot 
     *  ref-id="homepage" 
     *  flbCtxReported={(evt) => {
     *    console.log('> flb-ctxreported', evt);
     *  }}
     * ></flybits-slot>
     * @example
     * <!-- Vanilla HTML -->
     * const slot = new FlybitsSlot();
     * slot.addEventListener('flb-ctxreported', (evt) =>{
     *   console.log('> flb-ctxreported', evt?.detail);
     * });
     * @example
     * <!-- Vue -->
     * <flybits-slot 
     *  ref-id="homepage" 
     *  v-on:flb-ctxreported="(evt) => {
     *    console.log('> flb-ctxreported', evt?.detail);
     *  }"
     * ></flybits-slot>
     */
    flbCtxReported: {
      type: Function,
      attribute: false
    },
    /**
     * @instance
     * @memberof Flightkit.FlybitsSlot
     * @member {string[]} [styleFlags=[]] (HTML attribute: `style-flags`) Array of style flags to apply to the render layer of the FlybitsSlot element. 
     *
     * When set the inner content of the FlybitsSlot element will contain CSS selectable classes that can be used to style the content.
     
     * @example
     * <!-- declarative (vanilla HTML) -->
     * <flybits-slot ref-id="homepage" style-flags="special,offer"></flybits-slot>
     * @example
     * <!-- Vue -->
     * <flybits-slot ref-id="homepage" :style-flags="['special','offer']"></flybits-slot>
     * @example
     * // React
     * <flybits-slot ref-id="homepage" styleFlags={['special', 'offer']}></flybits-slot>
     * @example
     * // imperative
     * const slot = new FlybitsSlot();
     * slot.init({
     *   refID: 'homepage',
     *   styleFlags: ['special', 'offer']
     * });
     * @example
     * <!--
     * Now the inner content of the FlybitsSlot element will contain CSS
     * selectable classes that can be used to style the content.
     * -->
     * <style>
     *   .conatiner.special,.container.offer{
     *     background-color: red;
     *   }
     * </style>
     */
    styleFlags: {
      type: Array,
      attribute: 'style-flags',
      converter: commaSeparatedArray
    }
  };
  
  static styles = css`
    :host {
      position: relative;
      display: block;
    }
    .flb-frame {
      width: 100%;
      height: 100%;
      border: 0;
      z-index: 1;
    }
    .slot-default, ::slotted(*){
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.2s linear 0.2s, visibility 0s linear 0.2s;
      z-index: 2;
    }
    .slot-default.open, .open::slotted(*){
      visibility: visible;
      opacity: 1;
      transition: opacity 0.2s linear 0.2s, visibility 0s linear 0s;
    }
    .overlay-loading{
      display: none;
    }
  `

  /**
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @member {external:HTMLElement} $renderFrame Iframe that is used to render Flybits content, located within a FlybitsSlot element's {@link external:ShadowDOM|Shadow DOM}.
   */
  get $renderFrame() {
    return this.renderRoot?.querySelector('.flb-frame') ?? null;
  }

  /**
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @member {external:HTMLElement} $noContentUI Registered HTMLElement to be automatically displayed when a retrieved Flybits Zone contains no content.  This element is rendered outside of the {@link external:ShadowDOM|Shadow DOM} as a child of the FlybitsSlot element. 
   */
  get $noContentUI() {
    const slot = this.shadowRoot.querySelector('slot[name="no-content"]');
    const childElems = slot?.assignedElements({flatten: true});
    return childElems?.[0];
  }

  /**
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @member {external:HTMLElement|null} $overlayLoadingUI Clone of user defined override of default loading UI for a FlybitsOverlay.  This must be set via the `overlay-loading` slot.
   */
  get $overlayLoadingUI() {
    const slot = this.shadowRoot.querySelector('slot[name="overlay-loading"]');
    const childElems = slot?.assignedElements({flatten: true});
    if(childElems?.length){
      this._$overlayLoadingOverride = childElems[0];
    }
    return this._$overlayLoadingOverride;
  }

  /**
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @member {external:HTMLElement|null} $overlayErrorUI Clone of user defined override of default error UI for a FlybitsOverlay.  This must be set via the `overlay-error` slot.
   */
  get $overlayErrorUI() {
    const slot = this.shadowRoot.querySelector('slot[name="overlay-error"]');
    const childElems = slot?.assignedElements({flatten: true});
    if(childElems?.length){
      this._$overlayErrorOverride = childElems[0];
    }
    return this._$overlayErrorOverride;
  }

  get browserLocale(){
    var locale = navigator?.language || 'en';
    locale = locale.toLowerCase();
    if(locale.indexOf('-') > -1){
      locale = locale.split('-')[0];
    }
    return locale;
  }

  get resolvedLocale(){
    var retObj = {
      locale: '',
      textDirection: 'ltr'
    }
    if(this.localization === 'auto'){
      retObj.locale = this.browserLocale;
    } else if(this.localization?.length === 2){
      retObj.locale = this.localization;
    }
    switch(retObj.locale){
      case 'ar':
      case 'dv':
      case 'ff':
      case 'he':
      case 'ku':
      case 'fa':
      case 'ur':
        retObj.textDirection = 'rtl';
        break;
    }

    if(this.textDirection && ['ltr','rtl','auto'].indexOf(this.textDirection) > -1){
      retObj.textDirection = this.textDirection;
    }

    return retObj;
  }

  get initParams() {
    return {
      host: this.host,
      refID: this.refID,
      projectID: this.projectID
    }
  }

  constructor() {
    super();
    this._internalID = ObjUtil.guid(2);
    this.zone = null;
    this.pollingEnabled = false;
    this.pollingInterval = 2500;
    this.renderMode = FlybitsSlot.modes.DYNAMICHEIGHT;
    this.host = '';
    this.renderURL = cfg.RENDERHOST;
    this.refID = '';
    this.projectID = '';
    this._hasDeferredLoad = false;
    this.overlayDismissUI = false;
    this.overlayOutlet = document.body;
    this.overrideOverlay = false;
    this.overrideURLs = false;
    this.overrideActions = false;
    this._loaderStack = [];
    this.isLoadingOpen = false;
    this.isErrorOpen = false;
    this.showNoContentUI = false;
    this.hasLoadingSlot = false;
    this.hasErrorSlot = false;
    this.hasNoContentSlot = false;
    this._$overlayLoadingOverride = null;
    this._$overlayErrorOverride = null;
    this._$evtSrcCtx = this;
    this.logger = new Logger({
      mode: this.logLevel || 'errors'
    });
    this.localization = 'disabled';
    this.textDirection = null;
    this.exclude = [];
    this.styleFlags = [];
  }
  
  connectedCallback() {
    super.connectedCallback()
    this.frameLoadDef = new Deferred();
    this.frameLoadPromise = this.frameLoadDef.promise;
    this.detectFlybits();
    this._reboundHandleFrameEvent = this.handleFrameEvent.bind(this);
    window.addEventListener('message', this._reboundHandleFrameEvent);
    window.addEventListener('resize', this._handleWindowResize);
    this.frameLoadPromise.then(() => {
      this._handleWindowResize();
    });
  }
  
  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('message', this._reboundHandleFrameEvent);
    window.removeEventListener('resize', this._handleWindowResize);
  }

  /**
   * Initialization function for imperative approaches to FlybitsSlot usage.  This should be invoked immediately after FlybitsSlot instantiation.
   * @memberof Flightkit.FlybitsSlot
   * @instance
   * @function init
   * @param {Object} opts Initialization object containing FlybitsSlot class properties
   * @example
   * const slot = new FlybitsSlot();
   * slot.init({
   *  refID: 'homepage'
   * })
   */
  init(opts) {
    opts = opts ?? {}
    this.pollingEnabled = opts.pollingEnabled ?? this.pollingEnabled;
    this.pollingInterval = opts.pollingInterval || this.pollingInterval;
    this.host = opts.host || this.host;
    this.renderURL = opts.renderURL || this.renderURL;
    this.refID = opts.refID || this.refID;
    this.projectID = opts.projectID;
    this.overlayOutlet = opts.overlayOutlet || this.overlayOutlet;
    this.overrideOverlay = !!opts.overrideOverlay;
    this.overrideURLs = !!opts.overrideURLs;
    this.overrideActions = !!opts.overrideActions;
    this.logger.mode = this.logLevel = opts.logLevel || Logger.modes.ERRORS;
    if(opts.localization !== 'disabled'){
      const val = opts.localization?.toLowerCase();
      if(val === 'auto' || val?.length === 2){
        this.localization = val;
      }
    }
    if(opts.textDirection){
      switch(opts.textDirection){
        case 'ltr':
        case 'rtl':
        case 'auto':
          this.textDirection = opts.textDirection;
          break;
        default:
          this.logger.warn('invalid text direction specified:', opts.textDirection);
          break;
      }
    }
    if(opts.exclude?.length){
      this.exclude = toPlainArray(opts.exclude);
    }

    if(opts.styleFlags?.length){
      this.styleFlags = toPlainArray(opts.styleFlags);
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('exclude') && !isStructuredCloneable(this.exclude)) {
      this.exclude = toPlainArray(this.exclude);
    } else if (changedProperties.has('styleFlags') && !isStructuredCloneable(this.styleFlags)) {
      this.styleFlags = toPlainArray(this.styleFlags);
    }
  }

  initElementMeta() {
    if(this.renderMode !== FlybitsSlot.modes.DYNAMICHEIGHT){
      return;
    }
    if(!this.refID){
      this.logger.warn('missing Zone reference ID');
      return;
    }
    if(!Flybits.Session._projectID){
      this.logger.warn('missing Flybits project ID');
      return;
    }
    var $el = this;
    this.logger.log('fetching zone metadata', Flybits.Session._projectID);
    Flybits.api.Zones.getZoneMetadata(Flybits.Session._projectID, this.refID).then(function(meta){
      $el.logger.log('set from metadata height', meta.height);
      $el._setHeight(meta.height);
    }).catch(function(e){
      $el.logger.error(e);
    });
  }

  attributeChangedCallback(name, _old, value) {
    super.attributeChangedCallback(name, _old, value);
    if(name === 'ref-id'){
      if((!_old && value && !this._hasDeferredLoad) ||
         (_old && value && _old !== value)){
        this.detectFlybits();
      }
    } else if(name === 'log-level'){
      this.logger.mode = value;
    }
  }

  /**
   * Retrieves Zone data based on specified {@link Flightkit.FlybitsSlot.refID|Zone Reference ID} and injects it into the {@link Flightkit.FlybitsSlot.$renderFrame|internal render frame}.
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @function loadZoneData
   * @returns {external:Promise} a promise that will resolve or reject after Zone data is retrieved and injected into the {@link Flightkit.FlybitsSlot.$renderFrame|internal render frame}
   */
  loadZoneData() {
    const def = new Deferred();
    const $el = this;
    if(!this.refID || this.refID === 'overlay'){
      $el._hasDeferredLoad = false;
      $el.logger.warn('no refID detected');
      return Promise.reject();
    }
    
    this.showNoContentUI = false;
    this.isErrorOpen = false;
    this.setLoading(true);
    this.logger.log('fetching zone content', this.refID);
    ZoneAPI.getZoneContent(this.refID).then(function(zone){
      $el.zone = zone;
      $el.logger.log('set height from loadzone');
      $el._setHeight(zone.height);

      $el._dispatchLoad(zone);
      if(!ZoneUtil.hasContent(zone) && $el.$noContentUI){
        $el.showNoContentUI = true;
      }

      $el.injectData(zone.toConciergeContainer($el.resolvedLocale));
      def.resolve();
    }).catch(function(e){
      $el.logger.error(e);
      $el.isErrorOpen = true;
      $el._dispatchError(e);
      def.reject(e);
    }).then(function(){
      $el._hasDeferredLoad = false;
      $el.setLoading(false)
    });

    return def.promise;
  }

  injectData(payload){
    payload = {
      flb: true,
      type: 'flb.inject',
      appid: {
        name: 'flightkit',
        version: FlybitsSlot.VERSION,
        coreVersion: Flybits?.VERSION,
        logLevel: this.logLevel
      },
      // Plain copy so Vue/other reactive proxies survive structured clone in postMessage
      excludeContentLabels: toPlainArray(this.exclude),
      styleFlags: toPlainArray(this.styleFlags),
      ...payload
    }
    this._framePostMessage(payload);
  }

  _framePostMessage(payload){
    if(this.frameLoadPromise){
      this.frameLoadPromise.then(() => {
        var $frameWindow = this.$renderFrame?.contentWindow;
        if($frameWindow){
          this.logger.log('injecting payload', payload);
          $frameWindow.postMessage(payload, this.renderURL);
        } else{
          this.logger.error('no frame window detected');
        }
      })
    } else if(this.$renderFrame?.contentWindow){
      this.$renderFrame.contentWindow.postMessage(payload, $el.renderURL);
    }
  }

  /**
   * Handles {@link Flightkit.ActionLink|ActionLink} instances that could have originated from other FlybitsSlot instances.
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @function handleAction
   * @param {Flightkit.ActionLink} actionLink Instance of ActionLink containing information to perform actions and/or render Content details and full Zones.
   * @returns {external:Promise} A promise resolving after action handling.
   */
  handleAction(actionLink){
    const def = new Deferred();
    const $el = this;
    if(actionLink.protocol === ActionLink.protocol.DETAILS){
      if(!actionLink.params.contentId && actionLink.data.content && actionLink.meta.contentID){
        $el.logger.error('details action missing content id');
        return Promise.reject();
      }

      if(!actionLink.data.content){
        const contentID = actionLink.params.contentId || actionLink.meta.contentID;
        this._resolveContent(contentID).then(function(content){
          $el.injectData(Zone.generateDetailsPayload(content, $el.resolvedLocale));
          def.resolve();
        }).catch(function(e){
          $el.logger.error('cannot resolve content', e);
          def.reject();
        })
      } else{
        this.injectData(Zone.generateDetailsPayload(actionLink.data.content, $el.resolvedLocale));
        def.resolve();
      }
    } else if(actionLink.protocol === ActionLink.protocol.CONCIERGE){
      if(!actionLink.params.zoneReferenceId){
        $el.logger.error('concierge action missing zone reference id');
        return Promise.reject();
      }
      
      this.refID = actionLink.params.zoneReferenceId;
      return this.loadZoneData();
    }

    return def.promise;
  }

  /**
   * Changes the visibility of the loading state UI.
   * @instance
   * @memberof Flightkit.FlybitsSlot
   * @function setLoading
   * @param {boolean} bool State of loading overlay
   * @returns {Flightkit.FlybitsSlot} Returns the FlybitsSlot element this function was invoked upon.
   */
  setLoading(bool){
    if(bool){
      this.logger.log('open loading ui');
      this.isLoadingOpen = true;
      this._loaderStack.push(true);
    } else if(!bool && this._loaderStack.length > 1){
      this._loaderStack.pop();
    } else if(!bool){
      this.logger.log('closing loading ui');
      this._loaderStack.pop();
      this.isLoadingOpen = false;
    }
    return this;
  }

  _setHeight(height){
    if(height === null || height === undefined || isNaN(height)){
      this.logger.warn('not a valid height', height);
      return;
    }

    if(this.renderMode === FlybitsSlot.modes.DYNAMICHEIGHT){
      this.logger.log('setting height', height);
      this.style.height = height+'px';
    }
  }

  _resolveContent(contentID) {
    const def = new Deferred();
    const cachedContent = contentID?ZoneAPI.getCachedContentDetails(contentID):null;
    if(cachedContent){
      return Promise.resolve(cachedContent);
    }

    const $el = this;
    $el.setLoading(true);
    ZoneAPI.getContentDetails(contentID).then(function(results){
      def.resolve(results);
    }).catch(function(e){
      def.reject(e);
    }).then(function(){
      $el.setLoading(false);
    });
    return def.promise;
  }

  /**
   * Action Events which contain an {@link Flightkit.ActionLink|ActionLink} payload that support standard DOM API methods for event listening as utilizes the native DOM element CustomEvent class.
   * @event Flightkit.FlybitsSlot#action
   * @type {external:CustomEvent}
   * @property {Flightkit.ActionLink} detail ActionLink type payload
   * @property {string} type=action
   * @example
   * const slot = new FlybitsSlot();
   * slot.addEventListener('action', function(payload){
   *   const actionLink = payload.detail;
   * })
   */
  _dispatchActionLinks(actionLinkStr, metaObj) {
    const $el = this;
    const actionLink =  new ActionLink(actionLinkStr);
    
    if(!this.overrideActions){
      if(actionLink.protocol === ActionLink.protocol.DETAILS && !this.overrideOverlay){
        const contentID = actionLink.params.contentId || metaObj.contentID;
        this._resolveContent(contentID).then(function(content){
          actionLink.data.content = content;
          Flightkit.outlet.openOverlay(
            actionLink, 
            $el.overlayOutlet, 
            $el
          );
        }).catch(function(e){
          $el.logger.error('failed to load content',e);
        });
        return;
      }

      if(actionLink.protocol === ActionLink.protocol.CONCIERGE && !this.overrideOverlay){
        Flightkit.outlet.openOverlay(
          actionLink, 
          $el.overlayOutlet, 
          $el
        );
        return;
      }
  
      if(actionLink.protocol === ActionLink.protocol.HTTPS && !this.overrideURLs){
        if(!actionLink.rawStr){
          $el.logger.error('invalid url', actionLink.rawStr)
          return;
        }
  
        window.open(actionLink.rawStr, '_blank');
        return;
      }
  
      if(actionLink.protocol === ActionLink.protocol.BROWSER && !this.overrideURLs){
        if(!actionLink.params.url){
          $el.logger.error('invalid url', actionLink.params.url)
          return;
        }
  
        window.open(actionLink.params.url, '_blank');
        return;
      }

      if(actionLink.protocol === ActionLink.protocol.INAPPBROWSER && !this.overrideURLs){
        if(!actionLink.params.url){
          $el.logger.error('invalid url', actionLink.params.url)
          return;
        }

        window.location.href = actionLink.params.url;
        return;
      }
    }

    const payload = actionLink.toJSON();
    if(metaObj){
      payload.meta = metaObj;
    }

    const evt = new CustomEvent('action', {
      detail: payload,
      bubbles: true,
      composed: true
    });
    this._$evtSrcCtx.dispatchEvent(evt);
  }

  /**
   * Zone Load Event 
   * @event Flightkit.FlybitsSlot#zoneLoad
   * @type {external:CustomEvent}
   * @property {Object} detail Object containing event payload.
   * @property {Flybits.Zone} detail.zone Flybits Zone object containing modules and their respective content.
   * @property {string[]} detail.contentIDs An array of content IDs across all modules within the Zone.
   * @example
   * const slot = new FlybitsSlot();
   * slot.addEventListener('zoneLoad', function(payload){
   *   const actionLink = payload.detail;
   *   if(!payload?.contentIDs?.length){
   *     // Zone has no content
   *   } else if(!payload?.zone?.modules?.[1]?.content?.length){
   *     // Second module has no content
   *   }
   * })
   */
  _dispatchLoad(zone) {
    var cIDs = []
    zone?.modules?.forEach((m) => {
      m?.content?.forEach((c) => {
        cIDs.push(c.id);
      })
    });
    this._$evtSrcCtx.dispatchEvent(new CustomEvent('zoneLoad', {
      detail: {
        zone: zone,
        contentIDs: cIDs
      },
      bubbles: true,
      composed: true
    }))
  }

  /**
   * This event is dispatched when a Context Report originates from within the FlybitsSlot instance and is queued with the core Flybits.js SDK.
   * @event Flightkit.FlybitsSlot#flb-ctxreported
   * @type {external:CustomEvent}
   * @property {Flightkit.FlybitsSlot.CtxReportedEvent} detail Object containing event payload.
   * @example
   * <!-- Vanilla HTML -->
   * const slot = new FlybitsSlot();
   * slot.addEventListener('flb-ctxreported', function(evt){
   *   console.log('> flb-ctxreported', evt?.detail);
   * });
   * @example
   * <!-- Vue -->
   * <flybits-slot 
   *   ref-id="homepage" 
   *   v-on:flb-ctxreported="function(evt){
   *     console.log('> flb-ctxreported', evt?.detail);
   *   }"
   * ></flybits-slot>
   */
  _dispatchCtxReported(evt){
    if(typeof this.flbCtxReported === 'function'){
      this.flbCtxReported(evt);
    }
    this._$evtSrcCtx.dispatchEvent(new CustomEvent('flb-ctxreported', {
      detail: evt,
      bubbles: false,
      composed: true
    }));
  }

  /**
   * @typedef FlightkitError
   * @memberof Flightkit.FlybitsSlot
   * @type Object
   * @property {string} header Generally a short and broad error message
   * @property {string} message A more in depth explanation of the error.
   * @property {string} context This is populated if an error occurs that relates to one of the input properties of an operation and will be the property's key.
   * @property {number} code An internal error code indicating error type. This property is only populated when errors that can be discerned by the SDK occur. Errors that occur server side and cannot be discerned by the SDK will populate an HTTP status code in the `serverCode` property.  For instance, if you forget to supply required property the `code` property would be populated with `Flybits.Validation.type.MISSINGARG`.  On the other hand if there's a server outage, the `serverCode` would be populated with a 404 or 500.
   * @property {number} serverCode This is populated with an HTTP status code when a server side error occurs that cannot be discerned by the SDK.
   */
  /**
   * Flightkit Error Event
   * @event Flightkit.FlybitsSlot#error
   * @type {external:CustomEvent}
   * @property {Object} detail Object containing event payload.
   * @property {Flightkit.FlybitsSlot.FlightkitError[]} detail.errors List of errors
   */
  _dispatchError(validation){
    if(!validation || !(validation instanceof Flybits.Validation)){
      this.logger.error('invalid error type', validation);
      return;
    }

    delete validation.stacktrace;
    this._$evtSrcCtx.dispatchEvent(new CustomEvent('error', {
      detail: {
        errors: validation.errors
      },
      bubbles: true,
      composed: true
    }))
  }

  detectFlybits() {
    var $el = this;
    if(window.Flybits){
      CtxAPI.autoInitCtxManager();
      if(this.host){
        Flybits.cfg.HOST = this.host;
      }
      if(this.projectID){
        Flybits.Session._projectID = this.projectID;
      }

      if(Flybits.Session._projectID){
        this.initElementMeta();
        if(Flybits.Session.userToken){
          this.loadZoneData().catch(function(){})
        } else{
          if(!this._hasDeferredLoad){
            this._hasDeferredLoad = true;
            Flybits.Session.ready.then(function(){
              $el.loadZoneData().catch(function(){})
            })
          }
        }
      } else{
        if(!this._hasDeferredLoad){
          this._hasDeferredLoad = true;
          Flybits.Session.ready.then(function(){
            $el.loadZoneData().catch(function(){})
          })
        }
      }
    } else{
      this.logger.error('Flybits.js Core SDK not detected.');
    }
  }

  handleFrameEvent(payload) {
    try{
      var childContentWindow = this.$renderFrame.contentWindow;
      if(payload.source !== childContentWindow || payload.origin !== new URL(this.renderURL).origin){
        return;
      }

      if(payload.data && payload.data.flb){ 
        var eventObj = payload?.data?.value ? JSON.parse(payload.data.value):{};
        this.logger.log('frameEvt['+this.refID+']['+payload.data.type+']', eventObj)
        if(payload.data.type === 'context'){
          CtxAPI.handleContextPayload(eventObj, this.logger).then((sentArr) => {
            this._dispatchCtxReported({
              status: 'success',
              data: sentArr
            });
          }).catch((err) => {
            this._dispatchCtxReported({
              status: 'failed',
              data: [],
              validation: err
            });
          });
        } else if(payload.data.type === 'action'){
          this._dispatchActionLinks(eventObj.actionlink, {
            contentID: eventObj?.contentInfo?.id
          });
        } else if(payload.data.type === 'analytics'){
          CtxAPI.handleAnalyticsPayload(eventObj, this.logger).then((sentArr) => {
            this._dispatchCtxReported({
              status: 'success',
              data: sentArr
            });
          }).catch((err) => {
            this._dispatchCtxReported({
              status: 'failed',
              data: [],
              validation: err
            });
          });
        } else if(payload.data.type === 'error'){
        } else if(payload.data.type === 'dismiss'){
          if(typeof this._dispatchDismiss === 'function'){
            this._dispatchDismiss();
          }
        } else if(payload.data.type === 'flb.heightupdate'){
          const heightNum = +payload.data.value;
          this.logger.log('set height from frame event', heightNum);
          this._setHeight(heightNum);
        } else{
          // not supported messages  
          this.logger.warn('Message not supported', payload.data);
        }
      } else{
        // non-concierge messages  
      }
    } catch(e){
      this.logger.error('Cannot parse message', e);
    }
  }

  _handleWindowResize = ObjUtil.debounce(function(e){
    this.injectData({
      flb: true,
      type: 'flb.inject.parent.resize',
      outer: {
        width: window.outerWidth,
        height: window.outerHeight
      },
      inner: {
        width: window.innerWidth,
        height: window.innerHeight 
      },
      document: {
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight
      }
    });
  }.bind(this), 150)

  _handleFrameLoad(e) {
    this.frameLoadDef.resolve();
  }

  _handleLoadingSlotChange(e) {
    const childNodes = e.target.assignedNodes({
      flatten: true
    })
    this.logger.log('loading slot change', childNodes);
    if(childNodes.length){
      this.hasLoadingSlot =  true;
    }
  }

  _handleErrorSlotChange(e) {
    const childNodes = e.target.assignedNodes({
      flatten: true
    })
    this.logger.log('error slot change', childNodes);
    if(childNodes.length){
      this.hasErrorSlot =  true;
    }
  }

  _handleNoContentSlotChange(e) {
    const childNodes = e.target.assignedNodes({
      flatten: true
    })
    this.logger.log('no content slot change', childNodes);
    if(childNodes.length){
      this.hasNoContentSlot =  true;
    }
  }

  _handleSlotEvent(e){
    e.preventDefault();
    e.stopPropagation();
    if(e?.type === 'reload'){
      this.loadZoneData();
      this.isErrorOpen = false;
    }
  }

  render() {
    return html`
      <iframe src="${this.renderURL}" @load=${this._handleFrameLoad} frameborder="0" class="flb-frame" sandbox="allow-scripts allow-same-origin allow-downloads"></iframe>
      ${this.hasLoadingSlot?'':html`
        <div class=${classMap({open: this.isLoadingOpen,'slot-default':true,'loading': true})}>
          <flybits-loading></flybits-loading>
        </div>
      `}
      ${this.hasErrorSlot?'':html`
        <div class=${classMap({open: this.isErrorOpen,'slot-default':true,'error': true})}>
          <flybits-error @reload=${this._handleSlotEvent}></flybits-error>
        </div>
      `}
      <slot class=${classMap({open: this.isLoadingOpen})} name="loading" @slotchange=${this._handleLoadingSlotChange}></slot>
      <slot class=${classMap({open: this.isErrorOpen})} name="error" @reload=${this._handleSlotEvent} @slotchange=${this._handleErrorSlotChange}></slot>
      <slot class='overlay-loading' name="overlay-loading"></slot>
      <slot class='overlay-error' name="overlay-error"></slot>
      <slot class=${classMap({open: this.showNoContentUI})} name="no-content" @slotchange=${this._handleNoContentSlotChange}></slot>
    `
  }
}
customElements.define('flybits-slot', FlybitsSlot);